import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import referralService from "@/services/api/referral.service";
import { useNavigation } from "@/contexts/notification/NavigationContext";

const LowBalanceNotification = ({ isAdmin = false }) => {
  const navigate = useNavigate();
  const [lowBalanceData, setLowBalanceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(true);

  const { trackPageVisit, lastVisitedPages } = useNavigation();

  const checkLowBalance = async () => {
    try {
      setLoading(true);
      const response = await referralService.getLowBalanceAlert();

      if (response.status === "success" && response.data) {
        const { balance, threshold, isLowBalance } = response.data;

        if (isLowBalance && balance < threshold) {
          setLowBalanceData({
            balance,
            threshold,
            isLowBalance: true,
            lastAlert: response.data.lastAlert,
          });
        }
      }
    } catch (err) {
      console.error("Error checking low balance:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      checkLowBalance();

      // Set up periodic check every 5 minutes for admin
      const interval = setInterval(checkLowBalance, 5 * 60 * 1000);
      return () => clearInterval(interval);
    }
  }, [isAdmin]);

  const handleAddFunds = () => {
    const currentPath = window.location.pathname;

    if (lastVisitedPages.length === 0 || lastVisitedPages[0] !== currentPath) {
      trackPageVisit(currentPath);
    }

    if (isAdmin) {
      navigate("/admin/wallet/add-funds");
    } else {
      navigate("/transaction?action=add_funds");
    }
  };

  const handleViewTransactions = () => {
    const currentPath = window.location.pathname;

    if (lastVisitedPages.length === 0 || lastVisitedPages[0] !== currentPath) {
      trackPageVisit(currentPath);
    }

    if (isAdmin) {
      navigate("/admin/wallet/transactions");
    } else {
      navigate("/transaction");
    }
  };

  const handleClose = async () => {
    try {
      await referralService.dismissLowBalanceAlert();
      setIsVisible(false);
    } catch (error) {
      console.error("Error dismissing alert:", error);
      setIsVisible(false);
    }
  };

  if (
    loading ||
    !lowBalanceData ||
    !lowBalanceData.isLowBalance ||
    !isVisible
  ) {
    return null;
  }

  const { balance, threshold } = lowBalanceData;
  const criticalBalance = balance < 1000;

  return (
    <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-w-md w-full mx-4 overflow-hidden">
        <div
          className={`p-3 flex items-start justify-between ${
            criticalBalance ? "bg-red-50" : "bg-orange-50"
          }`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0 pt-0.5">
              <svg
                className={`h-5 w-5 ${
                  criticalBalance ? "text-red-600" : "text-orange-600"
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="ml-2">
              <p className="text-sm font-medium text-gray-900">
                {criticalBalance ? "Critical" : "Low"} Wallet Balance
              </p>
              <p className="text-xs text-gray-600 mt-0.5">
                {isAdmin ? "Admin wallet" : "Wallet"} balance: ₹
                {balance.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </p>
              {criticalBalance && (
                <p className="text-xs text-red-600 mt-0.5 font-medium">
                  Immediate action required
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="flex-shrink-0 ml-2 text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close notification"
          >
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        <div className="px-3 pb-2">
          <div className="w-full bg-gray-200 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full ${
                criticalBalance ? "bg-red-500" : "bg-orange-500"
              }`}
              style={{ width: `${Math.max((balance / threshold) * 100, 5)}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Threshold: ₹{threshold.toLocaleString("en-IN")}
          </p>
        </div>

        <div className="px-3 pb-3 flex gap-2">
          <button
            onClick={handleAddFunds}
            className={`flex-1 px-3 py-2 text-xs font-medium rounded ${
              criticalBalance
                ? "bg-red-600 text-white hover:bg-red-700"
                : "bg-orange-600 text-white hover:bg-orange-700"
            } transition-colors`}
          >
            Add Funds
          </button>
          <button
            onClick={handleViewTransactions}
            className="flex-1 px-3 py-2 text-xs font-medium border border-gray-300 rounded text-gray-700 hover:bg-gray-50 transition-colors"
          >
            View History
          </button>
        </div>
      </div>
    </div>
  );
};

export default LowBalanceNotification;
